// Wave 3.45 — Phase 1 — Jewellery Repair Workflow
// =============================================================================
// RepairItemsSection
// =============================================================================
// Drop-in section for Goldsmith Detail page that renders:
//   1) Header with summary pills (External / Internal weight totals).
//   2) A "+ Receive for repair" button that opens the RepairIntakeDialog.
//   3) Two grouped lists (External / Internal) of repair items currently
//      with this goldsmith.
//   4) Per-item edit (Gross→Detailed upgrade) action.
//
// Customer (external) valuables are tracked here as a FULLY SEPARATE
// sub-tree — they never co-mingle with the gold / diamond / CS / pearl
// "Materials With Goldsmith" balance trees (per the user's decision in
// plan.md § Wave 3.45 / Q2).
import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import {
  Wrench,
  Plus,
  Users,
  Package,
  Coins,
  Gem,
  Sparkles,
  Circle,
  ExternalLink,
  RefreshCw,
  Pencil,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { formatGrams, formatCarats, formatDateTime } from '@/lib/format';
import { RepairIntakeDialog } from './RepairIntakeDialog';
import { RepairUpgradeDialog } from './RepairUpgradeDialog';
import { RepairReceiveDialog } from './RepairReceiveDialog';

export function RepairItemsSection({ goldsmith, companyId, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [upgradeItem, setUpgradeItem] = useState(null);
  const [receiveItem, setReceiveItem] = useState(null);
  // Fix #2 — Collapse-by-default when there are no repair items issued to
  // this goldsmith; expand-by-default when items exist. User can override.
  const [expanded, setExpanded] = useState(false);
  const [userToggled, setUserToggled] = useState(false);

  const load = async () => {
    if (!goldsmith?.id || !companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/repairs/by-goldsmith/${goldsmith.id}/summary`, {
        params: { company_id: companyId },
      });
      setSummary(data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load repair items'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goldsmith?.id, companyId]);

  const totals = useMemo(() => {
    if (!summary) return { external: null, internal: null };
    return {
      external: summary?.external?.totals,
      internal: summary?.internal?.totals,
    };
  }, [summary]);

  const totalCount = useMemo(() => {
    const ext = summary?.external?.items?.length || 0;
    const intn = summary?.internal?.items?.length || 0;
    return ext + intn;
  }, [summary]);

  // Auto-expand when items are present, auto-collapse when empty.
  // Skip auto-toggle once the user has manually clicked the chevron so we
  // respect their last choice during the lifecycle of the page.
  useEffect(() => {
    if (userToggled) return;
    setExpanded(totalCount > 0);
  }, [totalCount, userToggled]);

  const handleCreated = () => {
    load();
    onChanged?.();
  };

  const handleUpgraded = () => {
    setUpgradeItem(null);
    load();
    onChanged?.();
  };

  const handleReceived = (updated) => {
    setReceiveItem(null);
    load();
    onChanged?.();
  };

  const handleReturnToCustomer = async (item) => {
    if (!window.confirm(`Mark "${item.title}" as returned to the customer? This will release the escrow.`)) {
      return;
    }
    try {
      await api.post(`/repairs/items/${item.id}/return-to-customer`);
      toast.success('Piece returned to customer; escrow released.');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to return to customer'));
    }
  };

  return (
    <Card className="p-5" data-testid="repairs-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <button
          type="button"
          onClick={() => { setUserToggled(true); setExpanded((v) => !v); }}
          className="flex items-start gap-2 text-left hover:opacity-80 transition-opacity"
          data-testid="repairs-section-toggle"
          aria-expanded={expanded}
        >
          <span className="mt-0.5 text-slate-500">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
          <div>
            <div className="flex items-center gap-2 text-base font-medium text-[var(--ink-navy)]">
              <Wrench size={16} />
              Customer Repair Items
              <Badge
                variant="secondary"
                className={`text-[10px] ml-1 ${totalCount > 0 ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-500'}`}
                data-testid="repairs-section-count-badge"
              >
                {totalCount} {totalCount === 1 ? 'item' : 'items'}
              </Badge>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Customer-owned (External) and Internal jewellery currently with{' '}
              <span className="font-medium text-slate-700">
                {goldsmith?.name || 'this goldsmith'}
              </span>{' '}
              for repair. Tracked separately from normal raw-material balances.
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-9 text-xs"
            onClick={load}
            disabled={loading}
            data-testid="repairs-reload-button"
          >
            <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
          <Button
            onClick={() => setIntakeOpen(true)}
            className="luxury-button h-9"
            data-testid="repairs-new-button"
          >
            <Plus size={14} className="mr-1.5" /> Receive for Repair
          </Button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Summary pills */}
          <div className="flex flex-wrap gap-2 mt-4" data-testid="repairs-summary-pills">
            <SummaryPill
              tone="external"
              icon={<Users size={12} />}
              label="External (customer)"
              totals={totals.external}
            />
            <SummaryPill
              tone="internal"
              icon={<Package size={12} />}
              label="Internal (our pieces)"
              totals={totals.internal}
            />
          </div>

          {/* Lists */}
          <div className="mt-4 space-y-4">
            <Bucket
              tone="external"
              label="External (Customer) items"
              items={summary?.external?.items || []}
              onEdit={setUpgradeItem}
              onReceive={setReceiveItem}
              onReturnToCustomer={handleReturnToCustomer}
            />
            <Bucket
              tone="internal"
              label="Internal items"
              items={summary?.internal?.items || []}
              onEdit={setUpgradeItem}
              onReceive={setReceiveItem}
              onReturnToCustomer={handleReturnToCustomer}
            />
          </div>
        </>
      )}

      <RepairIntakeDialog
        open={intakeOpen}
        onOpenChange={setIntakeOpen}
        goldsmith={goldsmith}
        companyId={companyId}
        onCreated={handleCreated}
      />

      {upgradeItem && (
        <RepairUpgradeDialog
          open={!!upgradeItem}
          onOpenChange={(v) => !v && setUpgradeItem(null)}
          item={upgradeItem}
          companyId={companyId}
          onSaved={handleUpgraded}
        />
      )}

      {receiveItem && (
        <RepairReceiveDialog
          open={!!receiveItem}
          onOpenChange={(v) => !v && setReceiveItem(null)}
          item={receiveItem}
          companyId={companyId}
          onCompleted={handleReceived}
        />
      )}
    </Card>
  );
}

function SummaryPill({ tone, icon, label, totals }) {
  const t = totals || {};
  const toneCls =
    tone === 'external'
      ? 'border-rose-200 bg-rose-50 text-rose-800'
      : 'border-emerald-200 bg-emerald-50 text-emerald-800';
  return (
    <span
      className={`inline-flex flex-wrap items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono tabular-nums ${toneCls}`}
      data-testid={`repairs-pill-${tone}`}
    >
      {icon}
      <span className="text-[10px] uppercase tracking-wide font-sans">{label}</span>
      <span className="font-medium">{t?.count || 0} item(s)</span>
      <span>· {formatGrams(t?.gross_weight_g || 0)} gross</span>
      {(t?.gold_grams || 0) > 0 && <span>· {formatGrams(t.gold_grams)} g (Au)</span>}
      {(t?.diamond_carats || 0) > 0 && (
        <span>· {formatCarats(t.diamond_carats)} ct (Dia)</span>
      )}
      {(t?.color_stone_carats || 0) > 0 && (
        <span>· {formatCarats(t.color_stone_carats)} ct (CS)</span>
      )}
      {(t?.pearl_carats || 0) > 0 && <span>· {formatCarats(t.pearl_carats)} ct (Pearl)</span>}
    </span>
  );
}

function Bucket({ tone, label, items, onEdit, onReceive, onReturnToCustomer }) {
  const isExternal = tone === 'external';
  const empty = !items.length;
  return (
    <div className="border border-[var(--divider)] rounded-md overflow-hidden">
      <div
        className={`px-3 py-1.5 text-xs font-medium flex items-center gap-2 ${
          isExternal ? 'bg-rose-50 text-rose-800' : 'bg-emerald-50 text-emerald-800'
        }`}
        data-testid={`repairs-bucket-${tone}-header`}
      >
        {isExternal ? <Users size={12} /> : <Package size={12} />}
        {label}
        <span className="text-[10px] opacity-75">({items.length})</span>
      </div>
      {empty ? (
        <div
          className="px-3 py-3 text-xs text-slate-500 italic"
          data-testid={`repairs-bucket-${tone}-empty`}
        >
          {isExternal
            ? 'No customer-owned repair items currently with this goldsmith.'
            : 'No internal repair items currently with this goldsmith.'}
        </div>
      ) : (
        <div className="divide-y divide-[var(--divider)]" data-testid={`repairs-bucket-${tone}-list`}>
          {items.map((it) => (
            <RepairItemRow
              key={it.id}
              item={it}
              onEdit={onEdit}
              onReceive={onReceive}
              onReturnToCustomer={onReturnToCustomer}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RepairItemRow({ item, onEdit, onReceive, onReturnToCustomer }) {
  const isGross = item.detail_mode === 'gross';
  const t = item.totals || {};
  const status = item.status || '';
  const canReceive = status === 'in_repair' || status === 'received';
  const canReturn = status === 'repaired';
  const canDownloadIntake = !!item.id;
  const canDownloadReturn = status === 'repaired' || status === 'returned_to_customer';

  const downloadVoucher = async (kind) => {
    try {
      const { data } = await api.get(`/repairs/items/${item.id}/voucher/${kind}.pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Repair_${kind}_${item.order_no || item.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e, `Failed to download ${kind} voucher`));
    }
  };

  return (
    <div
      className="px-3 py-2 flex flex-wrap items-start justify-between gap-2"
      data-testid={`repair-row-${item.id}`}
    >
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-medium text-[var(--ink-navy)]">{item.title}</div>
          <Badge
            variant="secondary"
            className={`text-[10px] ${isGross ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}
            data-testid={`repair-row-mode-${item.id}`}
          >
            {isGross ? 'Gross only' : 'Detailed'}
          </Badge>
          {item.order_no && (
            <Link
              to={`/orders/${item.order_id}`}
              className="font-mono text-[11px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline"
              data-testid={`repair-row-order-link-${item.id}`}
            >
              {item.order_no}
            </Link>
          )}
          <Badge
            variant="outline"
            className={`text-[10px] ${
              status === 'returned_to_customer'
                ? 'bg-slate-100 text-slate-700'
                : status === 'repaired'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : status === 'in_repair'
                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                    : ''
            }`}
            data-testid={`repair-row-status-${item.id}`}
          >
            {status.replace(/_/g, ' ')}
          </Badge>
        </div>
        {item.description && (
          <div className="text-[11px] text-slate-500 mt-0.5">{item.description}</div>
        )}
        {item.intake_type === 'external' && item.customer_name && (
          <div className="text-[11px] text-slate-500 mt-0.5">
            Customer: <span className="text-slate-700">{item.customer_name}</span>
            {item.customer_phone && (
              <span className="ml-1 text-slate-400">· {item.customer_phone}</span>
            )}
          </div>
        )}
        <div className="text-[10px] text-slate-400 mt-0.5">
          Received {formatDateTime(item.created_at)}
        </div>
      </div>

      {/* Numbers cluster */}
      <div className="flex flex-wrap items-start gap-2">
        <NumberPill
          icon={<Wrench size={11} />}
          label="Gross"
          value={`${formatGrams(t.incoming_gross_weight_g || 0)} g`}
        />
        {(t.gold_grams || 0) > 0 && (
          <NumberPill
            icon={<Coins size={11} className="text-amber-600" />}
            label="Gold"
            value={`${formatGrams(t.gold_grams)} g`}
          />
        )}
        {(t.diamond_carats || 0) > 0 && (
          <NumberPill
            icon={<Gem size={11} className="text-[var(--diamond-icon)]" />}
            label="Dia"
            value={`${formatCarats(t.diamond_carats)} ct · ${t.diamond_pieces} pc`}
          />
        )}
        {(t.color_stone_carats || 0) > 0 && (
          <NumberPill
            icon={<Sparkles size={11} className="text-pink-500" />}
            label="CS"
            value={`${formatCarats(t.color_stone_carats)} ct · ${t.color_stone_pieces} pc`}
          />
        )}
        {(t.pearl_carats || 0) > 0 && (
          <NumberPill
            icon={<Circle size={11} className="text-slate-500" />}
            label="Pearl"
            value={`${formatCarats(t.pearl_carats)} ct · ${t.pearl_pieces} pc`}
          />
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 flex-wrap">
          {isGross && status !== 'returned_to_customer' && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onEdit?.(item)}
              data-testid={`repair-row-upgrade-${item.id}`}
            >
              <Pencil size={11} className="mr-1" /> Add details
            </Button>
          )}
          {canReceive && (
            <Button
              size="sm"
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => onReceive?.(item)}
              data-testid={`repair-row-receive-${item.id}`}
            >
              <Wrench size={11} className="mr-1" /> Receive
            </Button>
          )}
          {canReturn && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => onReturnToCustomer?.(item)}
              data-testid={`repair-row-return-${item.id}`}
            >
              <Users size={11} className="mr-1" /> Return to Customer
            </Button>
          )}
          {canDownloadIntake && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-slate-600"
              onClick={() => downloadVoucher('intake')}
              data-testid={`repair-row-voucher-intake-${item.id}`}
              title="Download intake voucher"
            >
              📄 Intake
            </Button>
          )}
          {canDownloadReturn && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-slate-600"
              onClick={() => downloadVoucher('return')}
              data-testid={`repair-row-voucher-return-${item.id}`}
              title="Download return voucher"
            >
              📄 Return
            </Button>
          )}
          {item.order_id && (
            <Link
              to={`/orders/${item.order_id}`}
              className="inline-flex items-center text-[11px] text-[var(--ink-navy)] hover:underline px-1"
              data-testid={`repair-row-open-${item.id}`}
            >
              <ExternalLink size={11} className="mr-0.5" /> Open
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function NumberPill({ icon, label, value }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[var(--divider)] bg-slate-50/60 text-[10px] font-mono tabular-nums">
      {icon}
      <span className="text-slate-500 uppercase tracking-wide font-sans">{label}</span>
      <span className="text-slate-700">{value}</span>
    </span>
  );
}

export default RepairItemsSection;
